// src/components/enterprise/shared/measurement.jsx
//
// Single source of truth for the enterprise console's "discipline" UI:
// the leading/intermediate/lagging tag, the observed-vs-modeled tag, the
// reporting-floor constant, and the standard footer legend. Every enterprise
// tab should import these rather than re-inlining its own copy.

import React from 'react';
import { ShieldCheck } from 'lucide-react';

export const MIN_GROUP_SIZE = 5;          // reporting floor
export const RECOMMENDED_GROUP_SIZE = 10; // recommended for larger employers

const CONF = {
    Leading: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    Intermediate: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100',
    Lagging: 'text-amber-700 bg-amber-50 border-amber-100'
};

export const ConfidenceTag = ({ level, suffix }) => (
    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CONF[level] || CONF.Leading}`}>
        {level}{suffix ? ` ${suffix}` : ''}
    </span>
);

export const SourceTag = ({ modeled, dark }) => {
    if (dark) {
        return (
            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border text-blue-200 bg-white/10 border-white/20">
                {modeled ? 'Modeled' : 'Observed'}
            </span>
        );
    }
    return (
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
            modeled ? 'text-amber-700 bg-amber-50 border-amber-100' : 'text-[var(--brand-deep)] bg-blue-50 border-blue-100'
        }`}>
            {modeled ? 'Modeled' : 'Observed'}
        </span>
    );
};

export const DisciplineFooter = ({ caption, showLegend = true }) => (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex flex-col md:flex-row gap-4 items-start">
        <ShieldCheck size={20} className="text-[var(--brand-deep)] shrink-0 mt-0.5" />
        <div className="space-y-3">
            {showLegend && (
                <div className="flex flex-wrap gap-2">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Leading" /> 30–90 days · directly observable</span>
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Intermediate" /> 90 days–6 mo · directional</span>
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Lagging" /> 6–12 mo+ · needs baseline / cohort</span>
                </div>
            )}
            <p className="text-xs text-slate-500 font-light leading-relaxed max-w-3xl">
                {caption || 'EAS reports what it measures and labels what it models.'}
            </p>
        </div>
    </div>
);
